import { NetworkStatus } from "@apollo/client";
import { Error, UpToTop } from "@carbon/icons-react";
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import ScrollToTop from "react-scroll-to-top";

import PageLoader from "../../components/PageLoader";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import { deliveriesListTags } from "../../config/tagList";
import { useHeader } from "../../contexts/PageHeaderContext";
import { useUserContext } from "../../contexts/UserContext";
import useOutboundDeliveriesQuery from "../../hooks/graphql-hooks/queries/useOutboundDeliveriesQuery";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import useOwnerContext from "../../hooks/useOwnerContext";
import {
  OutboundDeliveryOverviewPagedFragment,
  Permissions,
  SortInput,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import { ITag } from "../../models/tag";
import TagFilter from "../deliveriesOverview/components/tagFilter/TagFilter";
import "./OutboundDeliveriesOverview.scss";
import OutboundDeliveryTable from "./components/outboundDeliveryTable/OutboundDeliveryTable";

const pageSize = 10;

interface OutboundDeliveriesQueryVariables {
  offset: number;
  limit: number;
  sort: SortInput;
  deliveryNumber?: string;
  recipientCompanyName?: string;
  senderCompanyName?: string;
  owner?: string;
}

const initialQueryParameters: OutboundDeliveriesQueryVariables = {
  offset: 0,
  limit: pageSize,
  sort: SortInput.Desc,
};

const OutboundDeliveriesOverview = () => {
  //#region Hooks (must be before any early returns)
  const { t } = useTranslation();
  const { updateHeader, updateSecondHeader } = useHeader();
  const { user } = useUserContext();
  const { getPrimaryOwner } = useOwnerContext();
  const [scrolledToBottom, resetScrolledToBottom] = useInfiniteScroll();
  const [outboundDeliveriesPaged, setOutboundDeliveriesPaged] = useState<
    OutboundDeliveryOverviewPagedFragment | undefined
  >();
  const [searchValue, setSearchValue] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const [
    outboundDeliveriesQuery,
    { loading, error, fetchMore, networkStatus },
  ] = useOutboundDeliveriesQuery({
    dataAction: (collection) => {
      setOutboundDeliveriesPaged(collection);
    },
  });
  //#endregion

  const outboundDeliveries = outboundDeliveriesPaged?.data ?? [];

  const tagNameFromUrl = searchParams.get("tag");
  const selectedTag =
    deliveriesListTags.find((tag) => tag.name === tagNameFromUrl) || null;

  const queryParameters = {
    ...initialQueryParameters,
    ...selectedTag?.inputParameters,
  };

  //#region Component functions
  const getOutboundDeliveries = useCallback(
    (queryParams: OutboundDeliveriesQueryVariables) => {
      outboundDeliveriesQuery({ variables: { input: queryParams } }).catch(
        () => {},
      );
    },
    [outboundDeliveriesQuery],
  );

  const selectTag = (tag: ITag) => {
    const newTag = tag.name === selectedTag?.name ? null : tag;
    setSearchParams(newTag ? { tag: newTag.name } : {});
  };

  const handleSearch = useCallback(
    (value: string) => {
      setSearchValue(value);
      // Reset pagination when search changes
      const newParams: OutboundDeliveriesQueryVariables = {
        ...initialQueryParameters,
        deliveryNumber: value || undefined,
        recipientCompanyName: value || undefined,
        senderCompanyName: value || undefined,
        owner: getPrimaryOwner(),
      };
      getOutboundDeliveries(newParams);
    },
    [getOutboundDeliveries, getPrimaryOwner],
  );

  const fetchMoreDeliveries = () => {
    if (!user.permissions.includes(Permissions.CanViewOutboundDelivery)) {
      resetScrolledToBottom();
      return;
    }

    if (!outboundDeliveriesPaged?.pageInfo?.hasNextPage) {
      resetScrolledToBottom();
      return;
    }

    const newParams: OutboundDeliveriesQueryVariables = {
      ...initialQueryParameters,
      offset: outboundDeliveries.length,
      deliveryNumber: searchValue || undefined,
      recipientCompanyName: searchValue || undefined,
      senderCompanyName: searchValue || undefined,
      owner: getPrimaryOwner(),
    };

    fetchMore({
      variables: { input: newParams },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          outboundDeliveries: {
            ...fetchMoreResult.outboundDeliveries,
            data: [
              ...prev.outboundDeliveries.data,
              ...fetchMoreResult.outboundDeliveries.data,
            ],
          },
        };
      },
    })
      .catch(() => {})
      .finally(() => resetScrolledToBottom());
  };

  useEffect(() => {
    if (user.permissions.includes(Permissions.CanViewOutboundDelivery)) {
      getOutboundDeliveries(queryParameters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    updateHeader(<>{t("yourDeliveries")}</>);
    updateSecondHeader(null);
  }, [t, updateHeader, updateSecondHeader]);

  useEffect(() => {
    if (scrolledToBottom) fetchMoreDeliveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrolledToBottom]);

  //#region Authorization check
  if (!user.permissions.includes(Permissions.CanViewOutboundDelivery)) {
    return (
      <FallbackComponent
        middleIcon={<Error size={32} />}
        text={t("unauthorized")}
      />
    );
  }
  //#endregion

  //#region Render functions
  const deliveryTable =
    networkStatus === NetworkStatus.loading ||
    (loading && networkStatus !== NetworkStatus.fetchMore) ? (
      <PageLoader />
    ) : (
      <OutboundDeliveryTable items={outboundDeliveries} />
    );

  if (error) {
    return (
      <FallbackComponent
        middleIcon={<Error size={32} />}
        text={t("outboundDeliveriesError")}
      />
    );
  }

  return (
    <div className="page-content outbound-deliveries-overview">
      <Helmet title={t("yourDeliveries")} />
      <TagFilter
        selectTag={selectTag}
        selectedTag={selectedTag}
        tagList={deliveriesListTags}
      />
      {deliveryTable}
      {networkStatus === NetworkStatus.fetchMore && (
        <PageLoader className="mt-15" text={t("loadingMoreEntries")} />
      )}
      <ScrollToTop style={{ bottom: "80px" }} smooth component={<UpToTop />} />
    </div>
  );
};
//#endregion
export default OutboundDeliveriesOverview;
